<template>
  <div
    class="select-menu"
    :class="{ 'select-menu--required': props.required }"
    :data-private-info="true"
  >
    <var-style-provider :style-vars="styleVars">
      <var-select
        ref="selectRef"
        v-model="inputValue"
        :aria-required="props.required"
        :placeholder="placeholder"
        :variant="variant"
        :offset-y="offsetY"
        :line="true"
        :rules="validateInput"
        @change="handleChange"
        :data-testid="props.selectTestId"
      >
        <var-option
          v-for="option in options"
          :label="option.label"
          :value="option.value"
        />
        <template #selected>
          <span>
            {{ options.find((option) => option.value == inputValue)?.label }}
          </span>
        </template>
      </var-select>
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import './SelectMenu.scss';
import { type LDTheme, type SelectMenuOption } from 'ewi-ui-helpers/types';

defineOptions({
  inheritAttrs: false,
});

export interface SelectMenuProps {
  options: SelectMenuOption[];
  modelValue?: any;
  theme?: LDTheme;
  placeholder?: string;
  variant?: 'outlined' | 'standard';
  offsetY?: number;
  selectTestId?: string;
  customErrorColor?: string;
  validateInput?: (value: string) => string | boolean;
  required?: boolean;
}

const props = withDefaults(defineProps<SelectMenuProps>(), {
  options: () => [],
  placeholder: 'Please Select one the options',
  modelValue: null,
  theme: 'light',
  variant: 'standard',
  offsetY: 34,
  selectTestId: 'select-menu',
});

const selectRef = ref();

const lightTheme = {
  '--field-decorator-text-color': 'var(--SysLightOnSurfaceVariant)',
  '--field-decorator-error-color': 'var(--SysLightError)',
  '--field-decorator-blur-color': 'var(--SysLightOnSurface)',
  '--field-decorator-focus-color': 'var(--SysLightPrimary)',
  '--field-background-color': 'var(--SysLightInverseOnSurface)',
  '--form-details-error-message-color':
    props.customErrorColor || 'var(--SysLightError)',
};

const darkTheme = {
  '--field-decorator-text-color': 'var(--SysDarkOnSurfaceVariant)',
  '--field-decorator-error-color': 'var(--SysDarkError)',
  '--field-decorator-blur-color': 'var(--SysDarkOnSurface)',
  '--field-decorator-focus-color': 'var(--SysDarkPrimary)',
  '--field-background-color': 'var(--SysDarkInverseOnSurface)',
  '--form-details-error-message-color':
    props.customErrorColor || 'var(--SysDarkError)',
};

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);

const emit = defineEmits(['update:modelValue', 'change']);
const inputValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newValue) => {
    inputValue.value = newValue;
  },
);

const handleChange = (value: any) => {
  emit('update:modelValue', value);
  emit('change', value);
};

defineExpose({
  validate: () => {
    // console.log('validate', selectRef?.value);
    return selectRef.value?.validate();
  },
});
</script>
